/* eslint-disable no-shadow */
/* eslint-disable react/no-unused-prop-types */
// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import Switch from 'react-switch';

import Select2, { DataSelect2, EventSelect2 } from 'wdna-select2';
import DatePicker from 'wdna-date-selector';

import { UserDO } from 'wdna-components';
import GranularitySelector from 'wdna-components/lib/datepicker/GranularitySelector';
import { parseDates, getBoundsOfDatesList, DateObject } from 'wdna-components/lib/helpers/date';
import { messageError } from 'wdna-components/lib/helpers/notification';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';

import { ContextType, FilterContext } from '../../../../../types';
import { company, CompanyDO } from '../../../../management/requests';
import { sensor, SensorListDO, RegisterInfoDO } from '../../../../sensors/requests';

const ALLOWED_GRANULARITIES: string[] = ['24h', '7d', '30d'];

interface Props {
  initialState?: any;
  autoRefresh?: boolean;
  requestFunc?: () => Promise<void>;
}

interface State {
  company: number[];
  companyArray: CompanyDO[];

  date: Date[];
  flatpickrMode: string;
  groupDateTime?: string;

  verified: number;
  autoRefresh: boolean;
}

export default class MainFilter extends React.PureComponent<Props, State> {
  private pid: string;
  private timer: NodeJS.Timer | undefined;
  private firstRender: boolean;
  private parentFilter: any;

  public static contextType: React.Context<ContextType> = FilterContext;
  public context: React.ContextType<typeof FilterContext> | undefined;

  public static defaultProps: Partial<Props> = {
    initialState: {},
    autoRefresh: undefined,
  };

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.firstRender = true;

    const { initialState = {}, autoRefresh } = props;

    /* INITIAL FILTER STATE */
    this.state = {
      autoRefresh,
      verified: 0,

      date: this.initialDates(),
      flatpickrMode: 'range',
      groupDateTime: '24h',

      companyArray: [],
      company: [],

      ...initialState,
    };
  }

  /* React Lifecycle methods */
  public componentDidMount(): void {
    this.load().catch(() => { /* pass */ });
    this.exportInitialData();
    this.refresh(this.props.autoRefresh || false);

    this.parentFilter = this.context?.parentState?.mainFilter;
  }

  public componentDidUpdate(): void {
    if (this.firstRender || !_.isEqual(this.parentFilter, this.context?.parentState?.mainFilter)) {
      this.parentFilter = this.context?.parentState?.mainFilter;
      this.fetchData().catch(() => { /* pass */ });
    }

    if (this.firstRender) this.firstRender = false;
  }

  public componentWillUnmount(): void {
    this.refresh(false);
  }

  public load = async (): Promise<void> => {
    const user: UserDO = _.get(window.store.getState(), ['user', 'user']);
    const userIsSuper: boolean = (user.type === 'adm' || user.superuser === 1);

    const companyFilter: Record<string, any> = {};
    if (!userIsSuper) companyFilter.name = user?.extra?.company;

    this.loadingStart();
    const companyArray: CompanyDO[] = await company<CompanyDO[]>('list', undefined, companyFilter) || [];
    if (companyArray) {
      // if (userIsSuper) companyArray.unshift({ id: 0, name: 'ALL' });
      this.setState({
        companyArray,
        company: userIsSuper ? [] : [companyArray[0].id],
      });
    }
    this.loadingFinish();
  };

  public hideSidebar = (): void => {
    $('#right-panel').addClass('hidden-functions');
    $('#tab-main-content').removeClass('tab-left-content');
    $('#toggle-func-btn').addClass('transparent sidebar-hidden');
  };

  public fetchData = async (): Promise<void> => {
    const { requestFunc } = this.props;
    if (typeof requestFunc !== 'function') return;

    this.loadingStart();
    try {
      const data: any = await requestFunc();
      this.context?.updateParent({
        update: window.getStr(),
        dashboardData: data
      });
    } catch (error: any) {
      messageError('Error', error.message);
    }
    this.loadingFinish();
    this.hideSidebar();
  };

  public loadingStart = (): void => {
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    window.store.dispatch(finishProcess(this.pid));
  };

  public refresh(create: boolean): void {
    if (this.props.autoRefresh !== undefined) {
      if (create) {
        this.timer = setInterval(() => { location.reload(); }, 600000);
      } else {
        clearInterval(this.timer);
      }
    }
  }

  public handleAutoRefresh = (autoRefresh: boolean): void => {
    this.setState({
      autoRefresh,
    }, () => { this.refresh(autoRefresh); });
  }

  public initialDates = (): Date[] => {
    const today: Date = new Date();
    const firstDayOfMonth: Date = new Date(today.getFullYear(), today.getMonth(), 1);

    return [firstDayOfMonth, today];
  }

  public exportInitialData = (): void => {
    this.setState({}, () => {
      this.exportMainFilter();
    });
  };

  public exportMainFilter = (): void => {
    const mainFilter: any = this.getFilterState();
    this.context?.updateParent({
      mainFilter,
    });
  };

  public getFilterState = (): any => {
    const { state } = this;

    const mainFilter: any = _.cloneDeep(state);

    const { groupDateTime, flatpickrMode, date } = mainFilter;

    const options: any = {
      granularity: groupDateTime,
      mode: flatpickrMode,
    };

    mainFilter.arrFilterDate = parseDates(date, options);

    if (flatpickrMode === 'multiple') {
      const boundDates: DateObject[] = getBoundsOfDatesList(mainFilter.arrFilterDate);
      mainFilter.arrFilterDate = boundDates;
    }

    return mainFilter;
  };

  /* Handlers */
  public handleChangeDatePicker = (config: any): void => {
    this.setState({
      date: config.getDates(),
      flatpickrMode: config.getMode(),
    });
  };

  public handleChangeGranularities = (groupDateTime: any): void => {
    this.setState({
      groupDateTime,
    });
  };

  public handleClick = (): void => {
    this.exportMainFilter();
  };

  public handleChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const { name, value } = e.target;
    this.setState((prevState: State) => ({
      ...prevState,
      [name]: value,
    }));
  };

  public handleChangeMultiple = (e: EventSelect2): void => {
    const { id } = e.params.data;
    const { selected } = e.params.data;
    if (selected) {
      this.addValue(parseInt(id, 10));
    } else {
      this.remValue(parseInt(id, 10));
    }
  };

  public addValue = (id: number): void => {
    if (this.isValueSelected(id)) return;
    const { company } = this.state || {};
    const selection: any[] = !company ? [] : company.slice();
    selection.push(id);
    this.setState({
      company: selection.sort((a: any, b: any) => {
        return a - b;
      }),
    });
  };

  public remValue = (id: number): void => {
    if (!this.isValueSelected(id)) return;
    const { company } = this.state || {};
    if (!company) return;
    const selection: any[] = company.filter((itemId: number) => itemId !== id);
    this.setState({
      company: selection,
    });
  };

  public isValueSelected = (id: number): boolean => {
    const { company } = this.state || {};
    if (!company) return false;
    return company.includes(id);
  };

  public render(): JSX.Element {
    const showMainRefresh: boolean = (this.props.autoRefresh !== undefined);
    const { autoRefresh, verified, date, flatpickrMode, groupDateTime, companyArray, company } = this.state;

    const user: UserDO = _.get(window.store.getState(), ['user', 'user']);
    const userIsAdmin: boolean = (user.type === 'adm');
    const userIsSuper: boolean = (user.type === 'adm' || user.superuser === 1);

    return (
      <div className="main-filters">
        {showMainRefresh && (
          <div className="auto-refresh-button">
            <br />
            <label htmlFor="normal-switch" style={{ marginRight: '20px' }}>
              <Switch
                id="auto-refresh-button"
                className="react-switch"
                aria-labelledby="neat-label"
                height={30}
                onColor="#78a329"
                offColor="#5c6875"
                offHandleColor="#444"
                onHandleColor="#b7f93d"
                checked={autoRefresh}
                onChange={this.handleAutoRefresh}
              />
              <span style={{ marginLeft: '10px' }}>Auto Refresh</span>
            </label>
          </div>
        )}
        <GranularitySelector
          active={groupDateTime}
          onChange={this.handleChangeGranularities}
          enabledGranularities={ALLOWED_GRANULARITIES}
        />
        <DatePicker
          icon
          hours={[]}
          dates={date}
          showChangeModes
          mode={flatpickrMode}
          granularity={groupDateTime}
          iconClass="wdna-ico-icon-calendar"
          handleChanges={this.handleChangeDatePicker}
        />
        <Select2
          name="company"
          multiple
          data={companyArray.map((item: CompanyDO): DataSelect2 => {
            return {
              id: item.id,
              text: item.name,
              title: item.name,
            };
          })}
          value={company}
          options={{
            placeholder: 'Select company',
          }}
          disabled={!userIsSuper}
          onSelect={this.handleChangeMultiple}
          onUnselect={this.handleChangeMultiple}
          style={{ width: '100%' }}
        />
        {userIsAdmin && (
          <div style={{ marginTop: '3px' }}>
            <Select2
              name="verified"
              data={[
                { id: 0, text: 'ALL' },
                { id: 1, text: 'VERIFIED' },
              ]}
              value={verified}
              options={{}}
              onChange={this.handleChange}
              style={{ width: '100%' }}
            />
          </div>
        )}
        <div style={{ marginTop: '1px' }}>
          <button type="button" className="filter-btn btn btn-default" onClick={this.handleClick}>
            <i className="wdna-ico-icon-playsearch" />
            <span>GO</span>
          </button>
        </div>
      </div>
    );
  }
}
